import { Box, SxProps, Theme } from '@mui/material'
import clsx from 'clsx'
import { LucideIcon, LucideProps } from 'lucide-react'
import { ComponentType, forwardRef, SVGProps } from 'react'

// Size mapping - matches MUI's fontSize values for SvgIcon
const SIZE_MAP = {
    sm: { fontSize: '1rem', className: 'MuiSvgIcon-fontSizeSmall' }, // 16px
    md: { fontSize: '1.5rem', className: 'MuiSvgIcon-fontSizeMedium' }, // 24px
    lg: { fontSize: '2.1875rem', className: 'MuiSvgIcon-fontSizeLarge' }, // 35px
} as const

type IconSize = keyof typeof SIZE_MAP

// Custom SVG component type (for non-Lucide icons)
type CustomSvgIcon = ComponentType<SVGProps<SVGSVGElement>>

// Base props shared by both variants
interface BaseC1IconProps {
    size?: IconSize
    sx?: SxProps<Theme>
    className?: string
    /** Optional overlay icon (e.g., Slash) to render on top of the main icon */
    overlayIcon?: LucideIcon
    /** Custom styles for the overlay icon */
    overlaySx?: SxProps<Theme>
}

// Props for Lucide icons (default)
interface LucideC1IconProps extends BaseC1IconProps, Omit<LucideProps, 'size' | 'color'> {
    icon: LucideIcon
    custom?: false
}

// Props for custom SVG icons
interface CustomC1IconProps extends BaseC1IconProps {
    icon: CustomSvgIcon
    custom: true
}

// Union type - TypeScript will enforce correct usage
export type C1IconProps = LucideC1IconProps | CustomC1IconProps

/**
 * Wrapper component that bridges Lucide icons (and custom SVG icons) with MUI theme styling.
 *
 * Uses MUI's icon class system (MuiSvgIcon-root, MuiSvgIcon-fontSizeSmall, etc.)
 * so that parent MUI components (Button, IconButton, etc.) can control icon
 * size and color via their existing CSS rules.
 *
 * @example
 * // Lucide icon (default)
 * <C1Icon icon={Plus} />
 * <C1Icon icon={Check} size="lg" sx={{ color: 'success.main' }} />
 *
 * // Custom SVG icon
 * <C1Icon icon={MyCustomIcon} custom />
 *
 * // Icon with overlay (e.g., "off" variant)
 * <C1Icon icon={Bell} overlayIcon={Slash} />
 */
export const C1Icon = forwardRef<HTMLSpanElement, C1IconProps>(({
    icon: Icon,
    size = 'sm',
    sx,
    className,
    custom,
    overlayIcon: OverlayIcon,
    overlaySx,
    ...props
}, ref) => (
    <Box
        ref={ref}
        component="span"
        className={clsx('MuiSvgIcon-root', SIZE_MAP[size].className, className)}
        sx={{
            // Match MUI SvgIcon base styles
            display: 'inline-block',
            fill: 'currentColor',
            width: '1em',
            height: '1em',
            fontSize: SIZE_MAP[size].fontSize,
            flexShrink: 0,
            userSelect: 'none',
            // Position relative when overlay is present
            position: OverlayIcon ? 'relative' : undefined,
            // Make the svg child fill this container
            '& > svg': {
                display: 'block',
                width: '100%',
                height: '100%',
            },
            ...sx,
        }}
    >
        {custom
            ? <Icon width="100%" height="100%" />
            : <Icon {...props as Omit<LucideProps, 'size' | 'color'>} />}
        {OverlayIcon && (
            <Box
                component="span"
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '& > svg': {
                        width: '100%',
                        height: '100%',
                    },
                    ...overlaySx,
                }}
            >
                <OverlayIcon />
            </Box>
        )}
    </Box>
))

C1Icon.displayName = 'C1Icon'

// Re-export for convenience
export type { LucideIcon, CustomSvgIcon }
